import { CloseOutlined } from "@ant-design/icons";
import { Alert, Button, Drawer } from "antd";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteSite } from "../../../api";
import { SiteWithBeacons } from "../../../api/models";
import { DeleteButton } from "../../../components/DeleteButton";
import { EditableText } from "../../../util/EditableText";
import { BeaconsWithProjectSite } from "../../../screens/construction-project/ConstructionProjectWrapper";
import { useNodeSelectedContext } from "../../../contexts/NodeSelectedContext";
import "../drawer.scss";
import { useUpdateConstructionSiteInformation } from "../useUpdateInformation";
import { BeaconDetails } from "./BeaconDetails";
import { SummaryInformation } from "./SummaryInformation";

interface ConstructionSiteDrawerProps {
  constructionSite: SiteWithBeacons | undefined;
  constructionProjectId: string;
  constructionProjectName: string;
  organizationId: string;
  beacons: BeaconsWithProjectSite[] | undefined;
  drawerVisible: boolean;
  onSave: (refreshTimeStamp: string) => void;
}

export function ConstructionSiteDrawer({
  constructionSite,
  constructionProjectId,
  constructionProjectName,
  organizationId,
  beacons,
  drawerVisible,
  onSave,
}: ConstructionSiteDrawerProps) {
  const { t } = useTranslation();
  const { setSelectedNode } = useNodeSelectedContext();

  useEffect(() => {
    if (!drawerVisible) {
      resetUpdateInfo();
    }
  }, [drawerVisible]);

  const deleteSite = useDeleteSite(constructionSite?.id || "");

  const showProjectDrawer = (projectId: string) => {
    setSelectedNode({
      selectedProjectId: projectId,
      projectDrawerVisible: true,
      siteDrawerVisible: false,
    });
  };

  const { updateResult, updateSiteName, resetUpdateInfo } =
    useUpdateConstructionSiteInformation(constructionSite?.id, onSave);

  const filteredSiteBeacons = useMemo(() => {
    if (!beacons) {
      return undefined;
    }
    const filteredBeacons = beacons.filter(
      (beacon) => beacon.siteId === constructionSite?.id
    );
    return filteredBeacons;
  }, [beacons, drawerVisible]);

  return (
    <Drawer
      title={
        <div data-testid="construction-site-drawer">
          <div className="title-container">
            <EditableText
              text={constructionSite?.name}
              style={{ width: "450px" }}
              saveCallback={(value) => updateSiteName(value)}
            />

            <Button
              type="text"
              className="close-card-drawer-button"
              icon={<CloseOutlined />}
              onClick={() => setSelectedNode({ siteDrawerVisible: false })}
            />
          </div>
        </div>
      }
      placement="right"
      closable={false}
      onClose={() => setSelectedNode({ siteDrawerVisible: false })}
      open={drawerVisible}
      width={600}
      destroyOnClose
    >
      {updateResult.status === 400 &&
        updateResult.updatedValue.key === "siteName" && (
          <Alert
            message={t("construction-site:update-site-name-failed")}
            type="error"
            closable
          />
        )}
      {updateResult.status === 200 &&
        updateResult.updatedValue.key === "siteName" && (
          <Alert
            message={t("construction-site:update-site-name-success")}
            type="success"
            closable
          />
        )}
      <>
        <SummaryInformation
          dateCreated={new Date(constructionSite?.createdAt ?? "") || ""}
          project={constructionProjectName}
          projectId={constructionProjectId}
          handleProjectClicked={(projectId) => {
            showProjectDrawer(projectId);
          }}
        />
        {drawerVisible && (
          <BeaconDetails
            constructionSiteId={constructionSite?.id ?? ""}
            siteBeacons={filteredSiteBeacons}
            organizationId={organizationId}
          />
        )}
      </>
      <DeleteButton
        name={constructionSite?.name}
        onDelete={deleteSite}
        refresh={() => {
          onSave(new Date().toISOString());
          drawerVisible = false;
          setSelectedNode({ siteDrawerVisible: false });
        }}
      />
    </Drawer>
  );
}
